/**
 * Schema-driven CRUD system
 * Automatically generates CRUD interfaces from OpenAPI schema
 */

import { Badge } from '@/components/ui/badge';

export interface SchemaField {
	name: string;
	type: string;
	format?: string;
	nullable?: boolean;
	required?: boolean;
	enum?: string[];
}

export interface SchemaDefinition {
	fields: SchemaField[];
	displayFields: string[]; // Fields to show in list view
	searchFields: string[]; // Fields to search on
	formFields: string[]; // Fields to show in create/edit form
}

// Define schema configurations for each entity
export const SCHEMA_CONFIGS: Record<string, SchemaDefinition> = {
	users: {
		fields: [],
		displayFields: ['username', 'email', 'name', 'surname', 'verified', 'created_at'],
		searchFields: ['username', 'email', 'name', 'surname'],
		formFields: ['email', 'username', 'name', 'surname', 'phone', 'verified'],
	},
	roles: {
		fields: [],
		displayFields: ['key', 'display_name', 'status'],
		searchFields: ['key', 'display_name'],
		formFields: ['key', 'display_name', 'status'],
	},
	blockchains: {
		fields: [],
		displayFields: ['name', 'network', 'chain_id', 'native_currency', 'is_testnet', 'is_active'],
		searchFields: ['name', 'network'],
		formFields: [
			'name',
			'network',
			'chain_id',
			'native_currency',
			'rpc_url',
			'explorer_url',
			'contract_address',
			'is_testnet',
			'is_active',
		],
	},
	tokens: {
		fields: [],
		displayFields: ['display_name', 'slug', 'type', 'contract_address', 'status'],
		searchFields: ['display_name', 'slug', 'contract_address'],
		formFields: ['display_name', 'slug', 'contract_address', 'blockchain_id', 'type', 'status'],
	},
	methodologies: {
		fields: [],
		displayFields: ['name', 'methodology_type', 'category', 'standard', 'approval_status', 'is_active'],
		searchFields: ['name', 'category', 'standard'],
		formFields: [
			'name',
			'methodology_type',
			'description',
			'category',
			'standard',
			'calculation_formula',
			'approval_status',
			'is_active',
			'is_public',
		],
	},
	contracts: {
		fields: [],
		displayFields: ['name', 'address', 'type', 'status'],
		searchFields: ['name', 'address'],
		formFields: ['name', 'address', 'blockchain_id', 'type', 'abi', 'status'],
	},
	requests: {
		fields: [],
		displayFields: ['role_key', 'requester_id', 'status', 'memo', 'created_at'],
		searchFields: ['role_key', 'requester_id', 'memo'],
		formFields: ['role_key', 'requester_id', 'role_id', 'status', 'memo', 'payload'],
	},
};

// Map OpenAPI types to form input types
export function getInputType(field: SchemaField): 'text' | 'email' | 'number' | 'textarea' | 'select' | 'checkbox' | 'date' {
	if (field.enum) return 'select';
	if (field.type === 'boolean') return 'checkbox';
	if (field.type === 'number' || field.type === 'integer') return 'number';
	if (field.format === 'email') return 'email';
	if (field.format === 'date' || field.format === 'date-time') return 'date';
	if (field.name.includes('description') || field.name.includes('formula')) return 'textarea';
	return 'text';
}

// Generate display label from field name
export function getFieldLabel(fieldName: string): string {
	return fieldName
		.split('_')
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
		.join(' ');
}

// Render function for common field types
export function getFieldRenderer(fieldName: string, fieldType: string) {
	// Boolean fields
	if (fieldType === 'boolean') {
		return (item: any) =>
			item[fieldName] ? (
				<Badge className="bg-green-100 text-green-800">Sim</Badge>
			) : (
				<Badge variant="outline">Não</Badge>
			);
	}

	// Status fields
	if (fieldName === 'status') {
		return (item: any) => (
			<Badge variant={item[fieldName] === 'active' ? 'default' : 'outline'}>
				{item[fieldName] === 'active' ? 'Ativo' : item[fieldName] === 'inactive' ? 'Inativo' : item[fieldName]}
			</Badge>
		);
	}

	// Verified field
	if (fieldName === 'verified') {
		return (item: any) =>
			item[fieldName] ? (
				<Badge className="bg-green-100 text-green-800">Verificado</Badge>
			) : (
				<Badge variant="outline">Não Verificado</Badge>
			);
	}

	// Testnet field
	if (fieldName === 'is_testnet') {
		return (item: any) =>
			item[fieldName] ? (
				<Badge variant="outline">Testnet</Badge>
			) : (
				<Badge className="bg-blue-100 text-blue-800">Mainnet</Badge>
			);
	}

	// Date fields
	if (fieldName.includes('_at') || fieldName.includes('date')) {
		return (item: any) => {
			const date = item[fieldName];
			return date ? new Date(date).toLocaleDateString('pt-BR') : '-';
		};
	}

	// Contract addresses (shorten)
	if (fieldName.includes('address') && fieldName !== 'email') {
		return (item: any) => {
			const addr = item[fieldName];
			if (!addr) return '-';
			if (addr.length > 42) {
				return (
					<code className="px-2 py-1 bg-muted rounded text-xs">
						{addr.substring(0, 10)}...{addr.substring(addr.length - 8)}
					</code>
				);
			}
			return <code className="px-2 py-1 bg-muted rounded text-xs">{addr}</code>;
		};
	}

	// Default: return string value
	return (item: any) => String(item[fieldName] || '-');
}

// Get options for enum/select fields
export function getFieldOptions(fieldName: string): { value: string; label: string }[] {
	const optionsMap: Record<string, { value: string; label: string }[]> = {
		status: [
			{ value: 'active', label: 'Ativo' },
			{ value: 'inactive', label: 'Inativo' },
			{ value: 'deprecated', label: 'Descontinuado' },
		],
		methodology_type: [
			{ value: 'verification', label: 'Verificação' },
			{ value: 'valuation', label: 'Avaliação' },
			{ value: 'certification', label: 'Certificação' },
		],
		approval_status: [
			{ value: 'pending', label: 'Pendente' },
			{ value: 'approved', label: 'Aprovado' },
			{ value: 'rejected', label: 'Rejeitado' },
		],
		type: [
			{ value: 'erc20', label: 'ERC-20 (Fungível)' },
			{ value: 'erc721', label: 'ERC-721 (NFT)' },
			{ value: 'erc1155', label: 'ERC-1155 (Multi-Token)' },
			{ value: 'custom', label: 'Personalizado' },
		],
	};

	return optionsMap[fieldName] || [];
}
